import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import ConsolePanel from '../../components/ConsolePanel';
import StatusBadge from '../../components/StatusBadge';
import { C, FONT, frameSx, hexToRgba, prefersReducedMotion } from '../../theme/tokens';
import { MapConnection, MapSystem, MassStatus, TrackedConnection } from '../../types';
import { parseServerDate } from '../../services/time';

const MASS_COLORS: Record<MassStatus, string> = {
  fresh: C.green,
  reduced: C.gold,
  critical: C.red,
  unknown: C.textDim,
};

const NODE_SIZE = 64;
/** The mark is a wide, short box, not a square. Edges used to be anchored at
 * `y + NODE_SIZE / 2` — half the WIDTH — which put every join 13px below the
 * real centre, so lines visibly ran along the bottom edge of the boxes
 * instead of through them. */
const NODE_HEIGHT = NODE_SIZE * 0.6;
const COL_WIDTH = 140;
const ROW_HEIGHT = 90;
const CORNER_TICK = 9;

/** A connection edge is a conduit, not a static line: a dim hairline for
 * structure plus a colored dash flowing from the mass-colored source
 * toward its destination — the same "light as structure, never a filled
 * shape" idiom ScanFrame carries, applied to motion instead of a plain
 * straight arrow. An EOL wormhole additionally flickers — the one place
 * motion is allowed to say something rather than just look alive. */
const ConduitEdge: React.FC<{
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  eol: boolean;
  /** 1 for a freshly-logged connection, decaying toward 0.25 over a week —
   * a chain nobody has touched in five days should not read as live. */
  freshness: number;
  collapsed: boolean;
}> = ({ x1, y1, x2, y2, color, eol, freshness, collapsed }) => {
  const reduced = prefersReducedMotion();
  // A collapsed hole keeps its place in the layout — losing the edge would
  // silently redraw the chain — but stops flowing and drops to a ghost line.
  if (collapsed) {
    return (
      <Box
        component="line"
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        sx={{ stroke: hexToRgba(C.textDim, 0.35), strokeWidth: 1, strokeDasharray: '2 6' }}
      />
    );
  }
  return (
    <Box component="g" sx={{ opacity: freshness }}>
      <Box component="line" x1={x1} y1={y1} x2={x2} y2={y2} sx={{ stroke: hexToRgba(color, 0.18), strokeWidth: 1.5 }} />
      <Box
        component="line"
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        sx={{
          stroke: color,
          strokeWidth: 2,
          strokeDasharray: '5 9',
          filter: `drop-shadow(0 0 4px ${hexToRgba(color, 0.6)})`,
          animation: reduced
            ? 'none'
            : `conduitFlow 1.1s linear infinite${eol ? ', conduitFlicker 2.4s ease-in-out infinite' : ''}`,
          '@keyframes conduitFlow': { to: { strokeDashoffset: -28 } },
          '@keyframes conduitFlicker': {
            '0%, 42%, 100%': { opacity: 1 },
            '48%': { opacity: 0.3 },
            '54%': { opacity: 1 },
            '80%': { opacity: 1 },
            '84%': { opacity: 0.55 },
            '88%': { opacity: 1 },
          },
        }}
      />
    </Box>
  );
};

/** Age as opacity: full for the last 12h, floored at 0.25 after a week. A
 * chain is a perishable claim about the universe, and a diagram that draws
 * a month-old edge exactly like this morning's one lies about it. */
const WEEK_MS = 7 * 24 * 3600 * 1000;
export function freshnessOf(updatedAt: string, now = Date.now()): number {
  // updated_at is SQLite's zoneless UTC; parsed raw, a freshly-updated
  // connection reads as hours old (or hours in the future) depending on the
  // reader's offset — and this age is what dims a stale hole on the diagram.
  const age = now - (parseServerDate(updatedAt)?.getTime() ?? now);
  if (!Number.isFinite(age) || age <= 12 * 3600 * 1000) return 1;
  return Math.max(0.25, 1 - (age - 12 * 3600 * 1000) / WEEK_MS);
}

/** A bracket-cornered node mark, echoing ScanFrame's own corner language
 * instead of a plain rounded box — the diagram's nodes read as the same
 * console instrument as every other panel in the app. Home system gets
 * the wormhole-purple accent (the one deliberate non-cyan mark), same
 * "this is the anchor" signal `Dashboard` gives its own home row. */
const NodeMark: React.FC<{ x: number; y: number; label: string; home: boolean; dossier: string }> = ({
  x,
  y,
  label,
  home,
  dossier,
}) => {
  const accent = home ? C.wormhole : C.cyan;
  // Shared with edgeAnchor, so the mark and the lines that meet it can never
  // disagree about where its border is.
  const h = NODE_HEIGHT;
  const tick = (cx: number, cy: number, dx: number, dy: number) => (
    <path d={`M ${cx},${cy + dy * CORNER_TICK} L ${cx},${cy} L ${cx + dx * CORNER_TICK},${cy}`} stroke={accent} strokeWidth={1.5} fill="none" />
  );
  return (
    <g style={{ filter: `drop-shadow(0 0 3px ${hexToRgba(accent, 0.35)})` }}>
      {/* A node is 64px wide, so the label is usually a truncated J-code and
          nothing else. Hovering gives the dossier — class, compartment, the
          note someone left — which is the whole reason to prefer this over a
          generic mapper. Native SVG <title> rather than a JS tooltip: it
          survives touch, keyboard focus and the screen reader, and costs
          nothing on a diagram that redraws every render. */}
      <title>{dossier}</title>
      <rect x={x} y={y} width={NODE_SIZE} height={h} fill="none" stroke={hexToRgba(accent, 0.4)} />
      {tick(x, y, 1, 1)}
      {tick(x + NODE_SIZE, y + h, -1, -1)}
      <text x={x + NODE_SIZE / 2} y={y + h * 0.58} textAnchor="middle" fill={C.text} fontSize={10} fontFamily={FONT.mono}>
        {label}
      </text>
    </g>
  );
};

/**
 * An edge nobody logged.
 *
 * The presence poller already watches linked characters fly, and records
 * every J->J hop it actually witnessed (see services/presencePoller.ts). Those
 * observations were only ever shown as a separate list, so the diagram still
 * drew nothing but hand-logged connections — which meant the map needed the
 * same manual upkeep any other mapper needs, and went stale the moment
 * somebody forgot.
 *
 * Drawing them is the one thing a generic mapper structurally cannot do: it
 * has no telemetry, so its map is only ever as current as its most diligent
 * member. Here the chain draws itself from where the corp actually flew.
 *
 * Deliberately a different mark from a logged edge, never the same one: an
 * observation says two systems were connected when somebody flew it. It
 * carries no mass, no lifetime, and no promise the hole is still open — so
 * it must not borrow the confident styling of a connection a human inspected
 * and recorded.
 */
const ObservedEdge: React.FC<{ x1: number; y1: number; x2: number; y2: number; jumps: number }> = ({
  x1,
  y1,
  x2,
  y2,
  jumps,
}) => (
  <g>
    {/* `jumps` is jump_count, which is COUNT(*) of passes over this one
        normalised pair — how many times the corp flew it, NOT how far apart
        the two systems are. "6 hops flown" read as "six jumps away", which
        is the opposite of what this edge means: it is a direct connection
        somebody used six times. TrackedCompartment already renders it "6x";
        matching that wording keeps one number from meaning two things in
        the same vault. */}
    <title>{`Observed — flown ${jumps}×, not logged by hand`}</title>
    {/* Legibility, found by actually screenshotting this rather than
        trusting that "dim" would read: at 1px / 0.55 alpha / a 2-4 dash on
        a near-black ground these edges were the faintest thing on the
        canvas — the one thing on the diagram nobody else can tell you,
        drawn so it could barely be seen. Weaker than a logged edge is the
        point; invisible is a bug. Dash and colour still separate it
        unmistakably from the solid, mass-coloured logged conduits. */}
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={hexToRgba(C.cyan, 0.5)}
      strokeWidth={1.5}
      strokeDasharray="5 4"
      strokeLinecap="round"
    />
  </g>
);

/**
 * Where an edge should meet a node: on the border of its box, facing the
 * other end — not at its centre.
 *
 * Drawn centre-to-centre, every line ran *under* the node boxes and out the
 * far side, so a connection looked like it pierced the systems it joined
 * rather than touching them. Clipping to the border is also what makes the
 * dashed observed edges legible at a diagonal, where the overlap was worst.
 *
 * Walks the direction vector out from the centre until it first crosses one
 * of the box's four sides: whichever of the horizontal and vertical limits
 * is reached sooner is the side it exits through.
 */
export function edgeAnchor(from: { x: number; y: number }, to: { x: number; y: number }) {
  const cx = from.x + NODE_SIZE / 2;
  const cy = from.y + NODE_HEIGHT / 2;
  const dx = to.x + NODE_SIZE / 2 - cx;
  const dy = to.y + NODE_HEIGHT / 2 - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };

  const t = Math.min(
    dx === 0 ? Number.POSITIVE_INFINITY : NODE_SIZE / 2 / Math.abs(dx),
    dy === 0 ? Number.POSITIVE_INFINITY : NODE_HEIGHT / 2 / Math.abs(dy),
  );
  return { x: cx + dx * t, y: cy + dy * t };
}

export interface ObservedEdgeSpec {
  fromId: number;
  toId: number;
  jumps: number;
}

/**
 * Which witnessed hops belong on this vault's diagram.
 *
 * Observations arrive keyed by solar_system_id, while the layout works in
 * vault entry ids, so this is also the translation between the two. Three
 * rules, each covering a way the diagram would otherwise lie:
 *
 * - Both ends must be saved in THIS vault. There is nowhere on the diagram
 *   to put a system the vault does not hold.
 * - A hand-logged connection always wins. It carries mass and lifetime an
 *   observation cannot, so drawing both would double the edge and imply the
 *   weaker one added something.
 * - A pair appears once. recentConnections() already groups by the
 *   normalised pair, so this is a guard rather than a live dedupe — it is
 *   what keeps the function honest if it is ever handed raw hops, where a
 *   scout rolling a hole back and forth would stack identical edges.
 */
export function deriveObservedEdges(
  systems: MapSystem[],
  connections: MapConnection[],
  tracked: TrackedConnection[] | undefined,
): ObservedEdgeSpec[] {
  if (!tracked?.length) return [];
  const entryBySolarSystem = new Map(systems.map((s) => [s.solar_system_id, s.id]));
  const pairKey = (a: number, b: number) => [a, b].sort((x, y) => x - y).join('-');

  const logged = new Set(
    connections
      .filter((c) => c.to_map_system_id != null)
      .map((c) => pairKey(c.from_map_system_id, c.to_map_system_id as number)),
  );

  const seen = new Set<string>();
  const out: ObservedEdgeSpec[] = [];
  for (const t of tracked) {
    const fromId = entryBySolarSystem.get(t.from_system_id);
    const toId = entryBySolarSystem.get(t.to_system_id);
    if (!fromId || !toId || fromId === toId) continue;
    const key = pairKey(fromId, toId);
    if (logged.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push({ fromId, toId, jumps: t.jump_count });
  }
  return out;
}

const MASS_LEGEND: MassStatus[] = ['fresh', 'reduced', 'critical', 'unknown'];

/** What a node says when you hover it. The label on the mark is a truncated
 * J-code; this is the reason to be looking at this diagram rather than a
 * generic one. Only fields the vault already holds — no extra fetch for a
 * tooltip. */
function dossierFor(s: MapSystem): string {
  const lines = [s.label && s.label !== s.name ? `${s.label} (${s.name})` : s.name];
  if (s.system_class) lines.push(`Class ${s.system_class}`);
  if (s.is_home_system) lines.push('Home system');
  if (s.tag) lines.push(`Compartment: ${s.tag}`);
  if (s.notes) lines.push(s.notes.length > 140 ? `${s.notes.slice(0, 139)}…` : s.notes);
  return lines.join('\n');
}

/** Pure, stateless layout — no saved positions, nothing draggable. BFS from
 * the folder's home system (fallback: first system) assigns each connected
 * system a depth/column; systems with no path from home get appended in a
 * trailing "unconnected" column. Recomputed fresh every render straight
 * from the connection list. */
const ConnectionDiagram: React.FC<{
  systems: MapSystem[];
  connections: MapConnection[];
  /** Hops the presence poller witnessed. Optional so the diagram still works
   * anywhere tracking is off or the data hasn't loaded. */
  tracked?: TrackedConnection[];
}> = ({ systems, connections, tracked }) => {
  const observed = useMemo(() => deriveObservedEdges(systems, connections, tracked), [tracked, systems, connections]);

  // Observed edges feed the layout too, or a system reachable only by a hop
  // somebody flew would be stranded in the "unconnected" column while an
  // edge visibly runs to it.
  const { positions, width, height } = useMemo(
    () => computeLayeredLayout(systems, connections, observed),
    [systems, connections, observed],
  );

  return (
    <ConsolePanel
      title="Connection Diagram"
      accent={C.cyan}
      corners={false}
      // The panel itself is a flex item of the page stack, so it needs the
      // same min-width:0 the scroll container below does — otherwise the
      // panel grows to the SVG's intrinsic width before the inner box ever
      // gets the chance to scroll it.
      sx={{ ...frameSx(C.cyan), mt: 3, minWidth: 0 }}
      action={
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          {MASS_LEGEND.map((m) => (
            <StatusBadge key={m} label={m} accent={MASS_COLORS[m]} dim={m === 'unknown'} />
          ))}
          {/* Named in the legend because a dashed line that means "we flew
              this but nobody logged it" is not self-evident, and mistaking
              it for a logged hole would mean trusting mass data that does
              not exist. */}
          {/* Cyan to match the edge it describes — a grey swatch next to a
              cyan line is a legend that disagrees with the picture. */}
          {observed.length > 0 && <StatusBadge label="observed" accent={C.cyan} dim />}
        </Stack>
      }
    >
      {/* minWidth:0 is what makes overflowX actually scroll: this Box is a
          flex item, whose default min-width:auto lets the SVG's intrinsic
          width push the panel — and the page — past the viewport instead.
          Latent until the reader view stopped hiding the diagram behind a
          toggle; caught at 390px by preview/shoot.mjs. */}
      <Box sx={{ overflowX: 'auto', minWidth: 0 }}>
        <svg width={Math.max(width, 200)} height={Math.max(height, 120)}>
          {/* Observed edges are emitted FIRST so they paint under the logged
              ones: an observation is weaker evidence, so where the two cross
              the confident edge reads on top. SVG has no z-index — document
              order is paint order — and these used to be emitted last while
              a comment claimed they were underneath, which is the kind of
              thing that stays wrong because the code reads as if it is
              right. Same pair is never both, so this only shows at
              crossings; that is exactly where it matters. */}
          {observed.map((o) => {
            const from = positions.get(o.fromId);
            const to = positions.get(o.toId);
            if (!from || !to) return null;
            const a = edgeAnchor(from, to);
            const b = edgeAnchor(to, from);
            return (
              <ObservedEdge
                key={`obs-${o.fromId}-${o.toId}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                jumps={o.jumps}
              />
            );
          })}
          {connections.map((c) => {
            const from = positions.get(c.from_map_system_id);
            const to = c.to_map_system_id ? positions.get(c.to_map_system_id) : null;
            if (!from || !to) return null;
            const a = edgeAnchor(from, to);
            const b = edgeAnchor(to, from);
            return (
              <ConduitEdge
                key={c.id}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                color={MASS_COLORS[c.mass_status]}
                eol={c.life_status === 'eol'}
                freshness={freshnessOf(c.updated_at)}
                collapsed={!!c.collapsed_at}
              />
            );
          })}
          {systems.map((s) => {
            const pos = positions.get(s.id);
            if (!pos) return null;
            return (
              <NodeMark
                key={s.id}
                x={pos.x}
                y={pos.y}
                label={s.label || s.name}
                home={!!s.is_home_system}
                dossier={dossierFor(s)}
              />
            );
          })}
        </svg>
      </Box>
    </ConsolePanel>
  );
};

function computeLayeredLayout(
  systems: MapSystem[],
  connections: MapConnection[],
  observed: { fromId: number; toId: number }[] = [],
) {
  const adjacency = new Map<number, number[]>();
  for (const s of systems) adjacency.set(s.id, []);
  const link = (a: number, b: number) => {
    adjacency.get(a)?.push(b);
    adjacency.get(b)?.push(a);
  };
  for (const c of connections) {
    if (c.to_map_system_id == null) continue;
    link(c.from_map_system_id, c.to_map_system_id);
  }
  // Observed hops shape the layout as well as the drawing. Without this a
  // system reachable only by a hop somebody flew would sit in the trailing
  // "unconnected" column with an edge visibly running to it.
  for (const o of observed) link(o.fromId, o.toId);

  const home = systems.find((s) => s.is_home_system) ?? systems[0];
  const depth = new Map<number, number>();
  if (home) {
    const queue: number[] = [home.id];
    depth.set(home.id, 0);
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const neighbor of adjacency.get(current) ?? []) {
        if (!depth.has(neighbor)) {
          depth.set(neighbor, (depth.get(current) ?? 0) + 1);
          queue.push(neighbor);
        }
      }
    }
  }
  const maxDepth = Math.max(0, ...Array.from(depth.values()));
  const unconnectedCol = maxDepth + 1;

  const columns = new Map<number, number[]>();
  for (const s of systems) {
    const col = depth.get(s.id) ?? unconnectedCol;
    if (!columns.has(col)) columns.set(col, []);
    columns.get(col)!.push(s.id);
  }

  const positions = new Map<number, { x: number; y: number }>();
  for (const [col, ids] of columns) {
    ids.forEach((id, row) => {
      positions.set(id, { x: col * COL_WIDTH + 20, y: row * ROW_HEIGHT + 20 });
    });
  }

  const width = (unconnectedCol + 1) * COL_WIDTH + 40;
  const height = Math.max(...Array.from(columns.values()).map((ids) => ids.length), 1) * ROW_HEIGHT + 40;
  return { positions, width, height };
}


export default ConnectionDiagram;
